import axios from "axios";
import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Modal,
  CircularProgress,
  Snackbar,
  Alert,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { styled } from '@mui/material/styles';
import Papa from 'papaparse';


const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'rect(0 0 0 0)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

export const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

const ZipcodeUpload = () => {
  const [open, setOpen] = React.useState(false);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [availableWarehouses, setAvailableWarehouses] = useState([]);
  const [warehouseLoading, setWarehouseLoading] = useState(false);
  const [updateMode] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [missingRequiredColumns, setMissingRequiredColumns] = useState(false);

  // Fetch available warehouses when component mounts
  useEffect(() => {
    fetchWarehouses();
  }, []);

  const fetchWarehouses = async () => {
    try {
      setWarehouseLoading(true);
      const auth = JSON.parse(localStorage.getItem("auth")) || {};
      const { accessToken } = auth;
      
      const apiUrl = process.env.REACT_APP_BASE_API_URL;
      
      try {
        const response = await axios.get(
          `${apiUrl}/wms/api/warehouses`,
          {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            }
          }
        );
        
        // Extract warehouse names from response
        const warehouses = response.data.data || response.data || [];
        const warehouseNames = warehouses.map(warehouse => warehouse.name);
        setAvailableWarehouses(warehouseNames);
      } catch (error) {
        console.error('Error fetching warehouses:', error);
        // Set empty array but don't trigger error state
        setAvailableWarehouses([]);
      }
    } finally {
      setWarehouseLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const showNotification = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const validateCSV = (results) => {
    const errors = [];
    const headers = results.meta.fields || [];
    
    if (!headers.includes('Backend') || !headers.includes('Pincode')) {
      errors.push('CSV must contain exactly two columns: Backend and Pincode');
      setMissingRequiredColumns(true);
      return errors;
    }

    setMissingRequiredColumns(false);
    
    // Track unique warehouse names to validate against
    const warehouseNames = new Set();
    
    results.data.forEach((row, index) => {
      if (!row.Backend) {
        errors.push(`Row ${index + 1}: Missing backend name`);
      } else {
        // Add warehouse name to set for later validation
        warehouseNames.add(row.Backend);
      }
    });

    return errors;
  };

  const onFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        showNotification('Please upload a CSV file', 'error');
        return;
      }

      setFile(selectedFile);
      
      Papa.parse(selectedFile, {
        header: true,
        complete: (results) => {
          // Validate and show logs in modal
          const errors = validateCSV(results);
          setValidationErrors(errors);
          setPreviewData(results.data.slice(0, 5));
          setOpen(true);
        }
      });
    }
  };

  const handleSubmit = async () => {
    // Don't show warnings about validation errors
    setLoading(true);
    
    try {
      const auth = JSON.parse(localStorage.getItem("auth")) || {};
      const { accessToken } = auth;
      
      // Get username from auth object
      const username = auth.username || '';
      
      const apiUrl = process.env.REACT_APP_BASE_API_URL;
      
      // Create form data
      const formData = new FormData();
      formData.append('file', file);
      
      // Add update mode parameter
      formData.append('updateMode', updateMode ? 'true' : 'false');
      
      // Send the file to the server
      const response = await axios.post(
        `${apiUrl}/wms/api/zipcodes/upload`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'multipart/form-data',
            'X-Username': username
          },
          timeout: 180000, // 3 minute timeout for large files
        }
      );
      
      showNotification('File uploaded successfully!', 'success');
      setOpen(false);
      setFile(null);
      setPreviewData([]);
      setValidationErrors([]);
      
    
      
    } catch (error) {
      console.error('Upload error:', error);
      
      let errorMessage = 'Failed to upload file';
      
      // Handle specific error cases
      if (error.response) {
        const { status, data } = error.response;
        
        if (status === 400) {
          errorMessage = data.message || 'Invalid file format or content';
        } else if (status === 500) {
          errorMessage = data.message || 'Server error during upload';
        } else if (status === 404) {
          errorMessage = 'Upload endpoint not found. Please check server configuration.';
        } else if (status === 401) {
          errorMessage = 'Authentication error. Please log in again.';
        }
      } else if (error.request) {
        // The request was made but no response was received
        errorMessage = 'No response from server. Please check your network connection.';
      }
      
      showNotification(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        component="label"
        variant="outlined"
        startIcon={<CloudUploadIcon />}
        sx={{ ml: 1 }}
      >
        Upload CSV
        <VisuallyHiddenInput type="file" accept=".csv" onChange={onFileChange} />
      </Button>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Typography id="modal-modal-title" variant="h6" component="h2">
            Confirm Upload
          </Typography>
          
          {warehouseLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <CircularProgress size={24} />
              <Typography sx={{ ml: 2 }}>Loading warehouse data...</Typography>
            </Box>
          )}
        
          
          {previewData.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1">Preview (First 5 rows):</Typography>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ border: '1px solid #ddd', padding: '8px' }}>Backend</th>
                    <th style={{ border: '1px solid #ddd', padding: '8px' }}>Pincode</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.map((row, index) => (
                    <tr key={index}>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>{row.Backend}</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>{row.Pincode}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          )}

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button 
              onClick={() => setOpen(false)} 
              sx={{ mr: 2 }}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              onClick={handleSubmit}
              disabled={loading || missingRequiredColumns}
            >
              {loading ? <CircularProgress size={24} /> : 'Upload'}
            </Button>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ZipcodeUpload;
